import React, { useState, useEffect } from "react";
import GlobalStyles, { CommonClasses } from "../../global/GlobalStyle";
import { useAlert } from "../../contexts/AlertContext";
import { transactionAPI } from "../../services/api";
import { Transaction } from "../../types";
import {
  MdHistory,
  MdPending,
  MdCheckCircle,
  MdCancel,
  MdRefresh,
  MdSwapHoriz,
  MdAccountBalance,
  MdTrendingUp,
  MdCardGiftcard,
  MdSavings,
  MdSearch,
  MdFilterList,
  MdRemove,
} from "react-icons/md";

const Transactions = () => {
  const { addAlert } = useAlert();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [balanceTypeFilter, setBalanceTypeFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTransaction, setSelectedTransaction] =
    useState<Transaction | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  const HistoryIcon = MdHistory as React.FC<React.SVGProps<SVGSVGElement>>;
  const PendingIcon = MdPending as React.FC<React.SVGProps<SVGSVGElement>>;
  const CheckIcon = MdCheckCircle as React.FC<React.SVGProps<SVGSVGElement>>;
  const CancelIcon = MdCancel as React.FC<React.SVGProps<SVGSVGElement>>;
  const RefreshIcon = MdRefresh as React.FC<React.SVGProps<SVGSVGElement>>;
  const TransferIcon = MdSwapHoriz as React.FC<React.SVGProps<SVGSVGElement>>;
  const LedgerIcon = MdAccountBalance as React.FC<
    React.SVGProps<SVGSVGElement>
  >;
  const StakeIcon = MdTrendingUp as React.FC<React.SVGProps<SVGSVGElement>>;
  const BonusIcon = MdCardGiftcard as React.FC<React.SVGProps<SVGSVGElement>>;
  const EarningsIcon = MdSavings as React.FC<React.SVGProps<SVGSVGElement>>;
  const SearchIcon = MdSearch as React.FC<React.SVGProps<SVGSVGElement>>;
  const FilterIcon = MdFilterList as React.FC<React.SVGProps<SVGSVGElement>>;
  const RemoveIcon = MdRemove as React.FC<React.SVGProps<SVGSVGElement>>;

  const fetchTransactions = async () => {
    try {
      setIsLoading(true);
      const response = await transactionAPI.getTransactions();
      setTransactions(response.data);
    } catch (error: any) {
      addAlert({ type: "error", message: "Failed to fetch transactions" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
    // eslint-disable-next-line
  }, []);

  const filteredTransactions = transactions.filter((transaction) => {
    // Status filter
    if (filter !== "all" && transaction.status !== filter) return false;

    // Balance type filter
    if (
      balanceTypeFilter !== "all" &&
      transaction.balance_type !== balanceTypeFilter
    )
      return false;

    // Type filter
    if (typeFilter !== "all" && transaction.type !== typeFilter) return false;

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        transaction.description?.toLowerCase().includes(searchLower) ||
        transaction.txid?.toLowerCase().includes(searchLower) ||
        transaction.coin?.toLowerCase().includes(searchLower) ||
        transaction.network?.toLowerCase().includes(searchLower) ||
        transaction.amount.toString().includes(searchTerm) ||
        transaction.balance_type.toLowerCase().includes(searchLower) ||
        transaction.type.toLowerCase().includes(searchLower)
      );
    }

    return true;
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "confirmed":
        return <CheckIcon className="w-4 h-4 text-green-500" />;
      case "pending":
        return <PendingIcon className="w-4 h-4 text-yellow-500" />;
      case "failed":
      case "rejected":
        return <CancelIcon className="w-4 h-4 text-red-500" />;
      default:
        return <PendingIcon className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "confirmed":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
      case "pending":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
      case "failed":
      case "rejected":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "deposit":
        return <CheckIcon className="w-4 h-4 text-green-500" />;
      case "withdrawal":
        return <CancelIcon className="w-4 h-4 text-red-500" />;
      case "charge":
        return <RemoveIcon className="w-4 h-4 text-red-500" />;
      case "bonus":
        return <BonusIcon className="w-4 h-4 text-purple-500" />;
      case "transfer":
        return <TransferIcon className="w-4 h-4 text-blue-500" />;
      default:
        return <HistoryIcon className="w-4 h-4 text-gray-500" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "deposit":
        return "text-green-600 dark:text-green-400";
      case "withdrawal":
      case "charge":
        return "text-red-600 dark:text-red-400";
      case "bonus":
        return "text-purple-600 dark:text-purple-400";
      case "transfer":
        return "text-blue-600 dark:text-blue-400";
      default:
        return "text-gray-600 dark:text-gray-400";
    }
  };

  const getBalanceTypeIcon = (balanceType: string) => {
    switch (balanceType) {
      case "ledger":
        return <LedgerIcon className="w-4 h-4 text-blue-500" />;
      case "stake":
        return <StakeIcon className="w-4 h-4 text-green-500" />;
      case "bonus":
        return <BonusIcon className="w-4 h-4 text-purple-500" />;
      case "earnings":
        return <EarningsIcon className="w-4 h-4 text-orange-500" />;
      default:
        return <LedgerIcon className="w-4 h-4 text-gray-500" />;
    }
  };

  const getBalanceTypeColor = (balanceType: string) => {
    switch (balanceType) {
      case "ledger":
        return "text-blue-600 dark:text-blue-400";
      case "stake":
        return "text-green-600 dark:text-green-400";
      case "bonus":
        return "text-purple-600 dark:text-purple-400";
      case "earnings":
        return "text-orange-600 dark:text-orange-400";
      default:
        return "text-gray-600 dark:text-gray-400";
    }
  };

  const getBalanceTypeBgColor = (balanceType: string) => {
    switch (balanceType) {
      case "ledger":
        return "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800";
      case "stake":
        return "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800";
      case "bonus":
        return "bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800";
      case "earnings":
        return "bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800";
      default:
        return "bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800";
    }
  };

  // Calculate statistics
  const stats = {
    total: transactions.length,
    confirmed: transactions.filter((t) => t.status === "confirmed").length,
    pending: transactions.filter((t) => t.status === "pending").length,
    failed: transactions.filter(
      (t) => t.status === "failed" || t.status === "rejected"
    ).length,
    totalAmount: transactions
      .filter((t) => t.status === "confirmed")
      .reduce((sum, t) => {
        // For charges and withdrawals, subtract the amount
        if (t.type === "charge" || t.type === "withdrawal") {
          return sum - Number(t.amount);
        }
        // For deposits, bonuses, and transfers, add the amount
        return sum + Number(t.amount);
      }, 0),
  };

  const formatAmount = (transaction: Transaction) => {
    const amount = Number(transaction.amount);
    const formattedAmount = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 8,
    }).format(amount);

    // Add minus sign for charges and withdrawals
    if (transaction.type === "charge" || transaction.type === "withdrawal") {
      return `-${formattedAmount}`;
    }
    return formattedAmount;
  };

  const getAmountColor = (transaction: Transaction) => {
    if (transaction.type === "charge" || transaction.type === "withdrawal") {
      return "text-red-600 dark:text-red-400";
    }
    return "text-green-600 dark:text-green-400";
  };

  return (
    <div className={GlobalStyles.container}>
      {/* Header */}
      <div
        className={`${CommonClasses.flexBetween} max-sm:flex-col max-sm:items-start max-sm:gap-4`}
      >
        <div>
          <h1
            className={`${CommonClasses.textXl} font-bold ${CommonClasses.textGrayStrong} mb-2`}
          >
            Transaction History
          </h1>
          <p className={`${CommonClasses.textGray}`}>
            View your complete transaction history across all balance types.
          </p>
        </div>
        <button
          onClick={fetchTransactions}
          disabled={isLoading}
          className={`${GlobalStyles.buttonIconOutline} ${
            isLoading ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          <RefreshIcon
            className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`}
          />
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className={`${GlobalStyles.sectionWrap} text-center`}>
          <div className={`${CommonClasses.textXl} font-bold text-primary`}>
            {stats.total}
          </div>
          <div className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}>
            Total Transactions
          </div>
        </div>
        <div className={`${GlobalStyles.sectionWrap} text-center`}>
          <div className={`${CommonClasses.textXl} font-bold text-green-600`}>
            {stats.confirmed}
          </div>
          <div className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}>
            Confirmed
          </div>
        </div>
        <div className={`${GlobalStyles.sectionWrap} text-center`}>
          <div className={`${CommonClasses.textXl} font-bold text-yellow-600`}>
            {stats.pending}
          </div>
          <div className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}>
            Pending
          </div>
        </div>
        <div className={`${GlobalStyles.sectionWrap} text-center`}>
          <div className={`${CommonClasses.textXl} font-bold text-red-600`}>
            {stats.failed}
          </div>
          <div className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}>
            Failed
          </div>
        </div>
      </div>

      {/* Net Balance Card */}
      <div className={`${GlobalStyles.sectionWrap} mb-6`}>
        <div className="text-center">
          <h3
            className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-2`}
          >
            Net Transaction Balance
          </h3>
          <div
            className={`text-2xl font-bold ${
              stats.totalAmount >= 0 ? "text-green-600" : "text-red-600"
            }`}
          >
            {stats.totalAmount >= 0 ? "+" : ""}
            {new Intl.NumberFormat("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(stats.totalAmount)}{" "}
            USD
          </div>
          <p
            className={`${CommonClasses.textSm} ${CommonClasses.textGray} mt-2`}
          >
            Sum of all confirmed transactions (Deposits - Withdrawals - Charges)
          </p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className={`${GlobalStyles.sectionWrap} mb-6`}>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <div className={GlobalStyles.inputWrapper}>
              <SearchIcon className={GlobalStyles.inputIcon} />
              <input
                type="text"
                placeholder="Search by description, TXID, amount, coin, or type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={GlobalStyles.input}
              />
            </div>
          </div>

          {/* Filter Toggle */}
          <div className="flex gap-2 lg:col-span-2 lg:justify-end">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`${GlobalStyles.buttonOutline} flex items-center gap-2`}
            >
              <FilterIcon className="w-4 h-4" />
              Filters
              {(filter !== "all" ||
                balanceTypeFilter !== "all" ||
                typeFilter !== "all") && (
                <span className="bg-primary text-white rounded-full w-5 h-5 text-xs flex items-center justify-center">
                  {
                    [filter, balanceTypeFilter, typeFilter].filter(
                      (f) => f !== "all"
                    ).length
                  }
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Expandable Filters */}
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            {/* Status Filter */}
            <div>
              <label className={GlobalStyles.label}>Status</label>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className={GlobalStyles.input}
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="failed">Failed</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            {/* Type Filter */}
            <div>
              <label className={GlobalStyles.label}>Transaction Type</label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className={GlobalStyles.input}
              >
                <option value="all">All Types</option>
                <option value="deposit">Deposits</option>
                <option value="withdrawal">Withdrawals</option>
                <option value="charge">Charges/Fees</option>
                <option value="bonus">Bonuses</option>
                <option value="transfer">Transfers</option>
              </select>
            </div>

            {/* Balance Type Filter */}
            <div>
              <label className={GlobalStyles.label}>Balance Type</label>
              <select
                value={balanceTypeFilter}
                onChange={(e) => setBalanceTypeFilter(e.target.value)}
                className={GlobalStyles.input}
              >
                <option value="all">All Balances</option>
                <option value="ledger">Ledger</option>
                <option value="stake">Stake</option>
                <option value="bonus">Bonus</option>
                <option value="earnings">Earnings</option>
              </select>
            </div>
          </div>
        )}

        {/* Active Filters */}
        {(filter !== "all" ||
          balanceTypeFilter !== "all" ||
          typeFilter !== "all" ||
          searchTerm) && (
          <div className="flex flex-wrap gap-2 mt-4">
            {filter !== "all" && (
              <span className="px-2 py-1 bg-primary text-white text-xs rounded-full flex items-center gap-1">
                Status: {filter}
                <button onClick={() => setFilter("all")}>×</button>
              </span>
            )}
            {typeFilter !== "all" && (
              <span className="px-2 py-1 bg-primary text-white text-xs rounded-full flex items-center gap-1">
                Type: {typeFilter}
                <button onClick={() => setTypeFilter("all")}>×</button>
              </span>
            )}
            {balanceTypeFilter !== "all" && (
              <span className="px-2 py-1 bg-primary text-white text-xs rounded-full flex items-center gap-1">
                Balance: {balanceTypeFilter}
                <button onClick={() => setBalanceTypeFilter("all")}>×</button>
              </span>
            )}
            {searchTerm && (
              <span className="px-2 py-1 bg-primary text-white text-xs rounded-full flex items-center gap-1">
                Search: {searchTerm}
                <button onClick={() => setSearchTerm("")}>×</button>
              </span>
            )}
            <button
              onClick={() => {
                setFilter("all");
                setTypeFilter("all");
                setBalanceTypeFilter("all");
                setSearchTerm("");
              }}
              className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Transactions List */}
      <div className={GlobalStyles.tableContainer}>
        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className={`${CommonClasses.textGray} mt-4`}>
              Loading transactions...
            </p>
          </div>
        ) : filteredTransactions.length === 0 ? (
          <div className="text-center py-8">
            <HistoryIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className={`${CommonClasses.textGray} mb-2`}>
              No transactions found
            </p>
            <p className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}>
              {searchTerm ||
              filter !== "all" ||
              typeFilter !== "all" ||
              balanceTypeFilter !== "all"
                ? "Try adjusting your filters or search terms"
                : "Your transaction history will appear here"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className={GlobalStyles.table}>
              <thead className={GlobalStyles.tableHeader}>
                <tr>
                  <th className={GlobalStyles.tableHeaderCell}>Type</th>
                  <th className={GlobalStyles.tableHeaderCell}>Amount</th>
                  <th className={GlobalStyles.tableHeaderCell}>Balance</th>
                  <th className={GlobalStyles.tableHeaderCell}>Description</th>
                  <th className={GlobalStyles.tableHeaderCell}>Status</th>
                  <th className={GlobalStyles.tableHeaderCell}>Date</th>
                </tr>
              </thead>
              <tbody className={GlobalStyles.tableBody}>
                {filteredTransactions.map((transaction) => (
                  <tr
                    key={transaction.id}
                    className={`${GlobalStyles.tableRow} cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors`}
                    onClick={() => setSelectedTransaction(transaction)}
                  >
                    <td className={GlobalStyles.tableCell}>
                      <div className={`${CommonClasses.flexStart} gap-2`}>
                        {getTypeIcon(transaction.type)}
                        <div>
                          <span
                            className={`font-medium ${getTypeColor(
                              transaction.type
                            )}`}
                          >
                            {transaction.type.charAt(0).toUpperCase() +
                              transaction.type.slice(1)}
                          </span>
                          {transaction.coin && (
                            <div
                              className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                            >
                              {transaction.coin.toUpperCase()}
                              {transaction.network &&
                                ` • ${transaction.network.toUpperCase()}`}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className={GlobalStyles.tableCell}>
                      <div
                        className={`font-semibold ${getAmountColor(
                          transaction
                        )}`}
                      >
                        {formatAmount(transaction)}{" "}
                        {transaction.coin
                          ? transaction.coin.toUpperCase()
                          : "USD"}
                      </div>
                      {transaction.fee > 0 && (
                        <div
                          className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                        >
                          Fee: {transaction.fee}
                        </div>
                      )}
                    </td>
                    <td className={GlobalStyles.tableCell}>
                      <div className={`${CommonClasses.flexStart} gap-2`}>
                        {getBalanceTypeIcon(transaction.balance_type)}
                        <span
                          className={`font-medium capitalize ${getBalanceTypeColor(
                            transaction.balance_type
                          )}`}
                        >
                          {transaction.balance_type}
                        </span>
                      </div>
                    </td>
                    <td className={GlobalStyles.tableCell}>
                      <div className="max-w-xs">
                        <p
                          className={`${CommonClasses.textSm} ${CommonClasses.textGrayStrong} line-clamp-2`}
                          title={transaction.description || "No description"}
                        >
                          {transaction.description || "No description"}
                        </p>
                        {transaction.txid && (
                          <p
                            className={`${CommonClasses.textXs} ${CommonClasses.textGray} font-mono truncate`}
                            title={transaction.txid}
                          >
                            TXID: {transaction.txid}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className={GlobalStyles.tableCell}>
                      <div className={`${CommonClasses.flexStart} gap-2`}>
                        {getStatusIcon(transaction.status)}
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                            transaction.status
                          )}`}
                        >
                          {transaction.status.charAt(0).toUpperCase() +
                            transaction.status.slice(1)}
                        </span>
                      </div>
                    </td>
                    <td className={GlobalStyles.tableCell}>
                      <div
                        className={`${CommonClasses.textSm} ${CommonClasses.textGrayStrong}`}
                      >
                        {new Date(transaction.created_at).toLocaleDateString()}
                      </div>
                      <div
                        className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                      >
                        {new Date(transaction.created_at).toLocaleTimeString()}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Transaction Details Modal */}
      {selectedTransaction && (
        <div className={GlobalStyles.modalOverlay}>
          <div className={GlobalStyles.modalContainer}>
            <div
              className={`p-5 border-b border-gray-300 dark:border-gray-700 ${CommonClasses.flexBetween}`}
            >
              <h2
                className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong}`}
              >
                Transaction Details
              </h2>
              <button
                onClick={() => setSelectedTransaction(null)}
                className={`${GlobalStyles.buttonIconOutline}`}
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Transaction Type and Status */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p
                    className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                  >
                    Type
                  </p>
                  <div className={`${CommonClasses.flexStart} gap-2 mt-1`}>
                    {getTypeIcon(selectedTransaction.type)}
                    <p
                      className={`font-medium ${getTypeColor(
                        selectedTransaction.type
                      )}`}
                    >
                      {selectedTransaction.type.charAt(0).toUpperCase() +
                        selectedTransaction.type.slice(1)}
                    </p>
                  </div>
                </div>
                <div>
                  <p
                    className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                  >
                    Status
                  </p>
                  <div className={`${CommonClasses.flexStart} gap-2 mt-1`}>
                    {getStatusIcon(selectedTransaction.status)}
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                        selectedTransaction.status
                      )}`}
                    >
                      {selectedTransaction.status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Amount and Balance Type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p
                    className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                  >
                    Amount
                  </p>
                  <p
                    className={`font-semibold text-lg ${getAmountColor(
                      selectedTransaction
                    )}`}
                  >
                    {formatAmount(selectedTransaction)}{" "}
                    {selectedTransaction.coin
                      ? selectedTransaction.coin.toUpperCase()
                      : "USD"}
                  </p>
                  {selectedTransaction.fee > 0 && (
                    <p
                      className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                    >
                      Fee: {selectedTransaction.fee}
                    </p>
                  )}
                </div>
                <div>
                  <p
                    className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                  >
                    Balance Type
                  </p>
                  <div className={`${CommonClasses.flexStart} gap-2 mt-1`}>
                    {getBalanceTypeIcon(selectedTransaction.balance_type)}
                    <p
                      className={`font-medium capitalize ${getBalanceTypeColor(
                        selectedTransaction.balance_type
                      )}`}
                    >
                      {selectedTransaction.balance_type}
                    </p>
                  </div>
                </div>
              </div>

              {/* Description */}
              {selectedTransaction.description && (
                <div>
                  <p
                    className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                  >
                    Description
                  </p>
                  <p
                    className={`${
                      CommonClasses.textGrayStrong
                    } p-3 rounded-lg ${getBalanceTypeBgColor(
                      selectedTransaction.balance_type
                    )} border`}
                  >
                    {selectedTransaction.description}
                  </p>
                </div>
              )}

              {/* Coin and Network */}
              {(selectedTransaction.coin || selectedTransaction.network) && (
                <div className="grid grid-cols-2 gap-4">
                  {selectedTransaction.coin && (
                    <div>
                      <p
                        className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                      >
                        Coin
                      </p>
                      <p className={CommonClasses.textGrayStrong}>
                        {selectedTransaction.coin.toUpperCase()}
                      </p>
                    </div>
                  )}
                  {selectedTransaction.network && (
                    <div>
                      <p
                        className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                      >
                        Network
                      </p>
                      <p className={CommonClasses.textGrayStrong}>
                        {selectedTransaction.network.toUpperCase()}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Transaction ID */}
              {selectedTransaction.txid && (
                <div>
                  <p
                    className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                  >
                    Transaction ID
                  </p>
                  <p
                    className={`${CommonClasses.textGrayStrong} font-mono text-sm break-all p-3 bg-gray-100 dark:bg-gray-700 rounded-lg`}
                  >
                    {selectedTransaction.txid}
                  </p>
                </div>
              )}

              {/* Addresses */}
              {(selectedTransaction.from_address ||
                selectedTransaction.to_address) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedTransaction.from_address && (
                    <div>
                      <p
                        className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                      >
                        From Address
                      </p>
                      <p
                        className={`${CommonClasses.textGrayStrong} font-mono text-xs break-all`}
                      >
                        {selectedTransaction.from_address}
                      </p>
                    </div>
                  )}
                  {selectedTransaction.to_address && (
                    <div>
                      <p
                        className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                      >
                        To Address
                      </p>
                      <p
                        className={`${CommonClasses.textGrayStrong} font-mono text-xs break-all`}
                      >
                        {selectedTransaction.to_address}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Date */}
              <div>
                <p
                  className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                >
                  Date & Time
                </p>
                <p className={CommonClasses.textGrayStrong}>
                  {new Date(selectedTransaction.created_at).toLocaleString()}
                </p>
              </div>

              {selectedTransaction.confirmed_at && (
                <div>
                  <p
                    className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                  >
                    Confirmed At
                  </p>
                  <p className={CommonClasses.textGrayStrong}>
                    {new Date(
                      selectedTransaction.confirmed_at
                    ).toLocaleString()}
                  </p>
                </div>
              )}
            </div>
            <div className="p-5 border-t border-gray-300 dark:border-gray-700">
              <button
                onClick={() => setSelectedTransaction(null)}
                className={`${GlobalStyles.buttonPrimary} w-full`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transactions;
